import type { Translate } from '@/shared/i18n';

import { FORBIDDEN_STATUS_CODE, PAYMENT_REQUIRED_STATUS_CODE } from '@/shared/api/errors';
import { isErrorGuard } from '@/shared/lib/types';

/** Why a recording can't be saved however many times the save is retried. */
export type RecordingSaveBlocker = 'accessDenied' | 'outOfRecordingMinutes';

export const getRecordingSaveBlocker = (error: unknown): null | RecordingSaveBlocker => {
  if (isErrorGuard(error, PAYMENT_REQUIRED_STATUS_CODE)) return 'outOfRecordingMinutes';
  if (isErrorGuard(error, FORBIDDEN_STATUS_CODE)) return 'accessDenied';
  return null;
};

export const isScreenShareDenial = (error: unknown) =>
  error instanceof DOMException && ['AbortError', 'NotAllowedError'].includes(error.name);

export const getRecorderErrorMessage = (error: unknown, t: Translate): string => {
  if (isScreenShareDenial(error)) return t('recordingModals.recorder.error.cancelled');

  return error instanceof Error ? error.message : t('recordingModals.recorder.error.generic');
};

export const createSharingEndedError = (message: string) =>
  Object.assign(new Error(message), { name: 'SharingEndedError' });

const getErrorName = (error: unknown) =>
  error instanceof DOMException || error instanceof Error ? error.name : undefined;

/** The DOMException name of a failed capture (e.g. NotReadableError), or 'unknown' for anything unnamed. */
export const getMediaErrorType = (error: unknown) => {
  const name = getErrorName(error);
  return name && name !== 'Error' ? name : 'unknown';
};

// Chunk uploads use fetch and only carry the status in their message, e.g. "Recording chunk upload failed (503): …".
const getErrorStatus = (error: unknown): null | number => {
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const status = Number(error.status);
    if (Number.isFinite(status)) return status;
  }
  const match = error instanceof Error ? /\((\d{3})\)/.exec(error.message) : null;
  return match ? Number(match[1]) : null;
};

export const getUploadErrorType = (error: unknown) => {
  if (getErrorName(error) === 'UnsentDataLimitError') return 'unsentDataLimit';
  if (getErrorName(error) === 'AbortError') return 'aborted';
  if (error instanceof TypeError) return 'network';
  const status = getErrorStatus(error);
  // Same blocker as getRecordingSaveBlocker: a 402 at save means the recording minutes ran out, not a client error.
  if (status === PAYMENT_REQUIRED_STATUS_CODE) return 'outOfRecordingMinutes';
  if (status !== null && status >= 500) return '5xx';
  if (status !== null && status >= 400) return '4xx';
  return 'network';
};
